import React, { useEffect, useState, useMemo } from "react"
import Breadcrumb from "components/Common/Breadcrumb"
import { Card, CardBody, Button, Table, Input } from "reactstrap"
import { useNavigate } from "react-router-dom"
import { FaEdit, FaPlus, FaSearch } from "react-icons/fa"
import { MdDelete, MdCancel } from "react-icons/md"
import { fetchData, FetchDeleteData } from "utils/Common_api_functions"
import Loader from "components/Loader/Loader"
import moment from "moment"
import PermittedComponent from "PermittedComponent"
import { usePermissionData } from "AppContext"
import Swal from "sweetalert2"

const MONTHS = [
  { label: "All Months", value: "" },
  { label: "January", value: "01" },
  { label: "February", value: "02" },
  { label: "March", value: "03" },
  { label: "April", value: "04" },
  { label: "May", value: "05" },
  { label: "June", value: "06" },
  { label: "July", value: "07" },
  { label: "August", value: "08" },
  { label: "September", value: "09" },
  { label: "October", value: "10" },
  { label: "November", value: "11" },
  { label: "December", value: "12" },
]

const currentYear = new Date().getFullYear()
const YEARS = [
  { label: "All Years", value: "" },
  ...Array.from({ length: currentYear - 2025 + 1 }, (_, i) => ({
    label: String(2025 + i),
    value: String(2025 + i),
  })),
]

const ColdCallingIndex = () => {
  const navigate = useNavigate()

  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState("")
  const [selectedMonth, setSelectedMonth] = useState(
    String(moment().format("MM"))
  )
  const [selectedYear, setSelectedYear] = useState(String(moment().year()))

  const [dateFrom, setDateFrom] = useState("")
  const [dateTo, setDateTo] = useState("")
  const [employeeFilter, setEmployeeFilter] = useState("")
  const [employeeList, setEmployeeList] = useState([])

  // ── Pagination state ──────────────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)

  useEffect(() => {
    fetchColdCalls()
    fetchEmployees()
  }, [])

  async function fetchColdCalls() {
    const APIdata = await fetchData(setLoading, "cold/call")
    if (APIdata?.success) setData(APIdata?.data)
  }

  async function fetchEmployees() {
    const APIdata = await fetchData(setLoading, "users")
    if (APIdata?.success) setEmployeeList(APIdata?.data)
  }

  const filteredData = useMemo(() => {
    if (!data) return []

    let result = data

    if (selectedYear)
      result = result.filter(
        item => moment(item.date).format("YYYY") === selectedYear
      )

    if (selectedMonth)
      result = result.filter(
        item => moment(item.date).format("MM") === selectedMonth
      )

    if (dateFrom || dateTo) {
      const from = dateFrom ? new Date(dateFrom) : null
      const to = dateTo ? new Date(dateTo) : null
      if (from) from.setHours(0, 0, 0, 0)
      if (to) to.setHours(23, 59, 59, 999)

      result = result.filter(item => {
        const itemDate = item.date ? new Date(item.date) : null
        if (!itemDate) return false
        if (from && itemDate < from) return false
        if (to && itemDate > to) return false
        return true
      })
    }

    if (employeeFilter)
      result = result.filter(item => item?.addedById === Number(employeeFilter))

    if (search.trim()) {
      const q = search.toLowerCase()
      result = result.filter(item =>
        [
          item.name,
          item.email,
          item.type,
          item.machineName,
          item.area,
          item.addedBy?.name,
          moment(item.date).format("DD-MM-YYYY"),
        ]
          .filter(Boolean)
          .some(val => val.toLowerCase().includes(q))
      )
    }

    return result
  }, [search, selectedMonth, selectedYear, dateFrom, dateTo, employeeFilter, data])

  // ── Pagination computed values ────────────────────────────────────────────
  const totalPages = Math.ceil(filteredData.length / pageSize)
  const start = (currentPage - 1) * pageSize
  const paginatedData = filteredData.slice(start, start + pageSize)

  // Reset to page 1 whenever filters change
  const resetPage = () => setCurrentPage(1)

  const activeFilters = [selectedYear, selectedMonth, search.trim()].filter(
    Boolean
  ).length

  const handleDelete = (staff, id) => {
    Swal.fire({
      title: `Are you sure you want to delete ${staff?.name}?`,
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(async result => {
      if (result.isConfirmed) {
        const APIdata = await FetchDeleteData(setLoading, "cold/call", id)
        if (APIdata?.data?.success) {
          Swal.fire({
            title: "Cold Call Deleted Successfully.",
            text: APIdata?.data?.message,
            icon: "success",
          })
          fetchColdCalls()
        } else {
          Swal.fire({
            title: "Error Deleting Cold Call.",
            text: APIdata?.response?.data?.message,
            icon: "error",
          })
        }
      }
    })
  }

  const { checkAccess } = usePermissionData()

  return (
    <PermittedComponent isAccess={checkAccess(4, "view")}>
      <div className="page-content">
        {loading && <Loader />}
        <Breadcrumb
          title="Cold Calling Management"
          breadcrumbItem="Cold Calling"
        />

        <Card>
          <CardBody>
            {/* ── Top Bar ── */}
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              {/* Left — Page size + Search + Filters */}
              <div className="d-flex align-items-center gap-2 flex-wrap">

                {/* Page size selector */}
                <Input
                  type="select"
                  className="rounded-4"
                  value={pageSize}
                  style={{ width: 90 }}
                  onChange={e => {
                    setPageSize(Number(e.target.value))
                    resetPage()
                  }}
                >
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </Input>

                {/* Search */}
                <div className="position-relative">
                  <FaSearch
                    style={{
                      position: "absolute",
                      left: 10,
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "#aaa",
                      fontSize: 13,
                    }}
                  />
                  <Input
                    placeholder="Search name, email, type..."
                    style={{ width: 240, paddingLeft: 30 }}
                    className="rounded-4"
                    value={search}
                    onChange={e => {
                      setSearch(e.target.value)
                      resetPage()
                    }}
                  />
                  {search && (
                    <MdCancel
                      onClick={() => { setSearch(""); resetPage() }}
                      style={{
                        position: "absolute",
                        right: 10,
                        top: "50%",
                        transform: "translateY(-50%)",
                        cursor: "pointer",
                        color: "#aaa",
                        fontSize: 16,
                      }}
                    />
                  )}
                </div>

                {/* Employee filter */}
                <Input
                  type="select"
                  className="rounded-4"
                  style={{ width: 180 }}
                  value={employeeFilter}
                  onChange={e => { setEmployeeFilter(e.target.value); resetPage() }}
                >
                  <option value="">All Employees</option>
                  {employeeList.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.name}
                    </option>
                  ))}
                </Input>

                {/* Date From */}
                <div className="d-flex align-items-center gap-1">
                  <small className="text-muted fw-semibold">From</small>
                  <Input
                    type="date"
                    className="rounded-4"
                    style={{ width: 150 }}
                    value={dateFrom}
                    onChange={e => { setDateFrom(e.target.value); resetPage() }}
                  />
                </div>

                {/* Date To */}
                <div className="d-flex align-items-center gap-1">
                  <small className="text-muted fw-semibold">To</small>
                  <Input
                    type="date"
                    className="rounded-4"
                    style={{ width: 150 }}
                    value={dateTo}
                    onChange={e => { setDateTo(e.target.value); resetPage() }}
                  />
                </div>

                {/* Month Dropdown */}
                <Input
                  type="select"
                  className="rounded-4"
                  style={{
                    width: 145,
                    borderColor: selectedMonth ? "#be9545" : undefined,
                    color: selectedMonth ? "#be9545" : undefined,
                    fontWeight: selectedMonth ? 600 : undefined,
                  }}
                  value={selectedMonth}
                  onChange={e => { setSelectedMonth(e.target.value); resetPage() }}
                >
                  {MONTHS.map(m => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </Input>

                {/* Year Dropdown */}
                <Input
                  type="select"
                  className="rounded-4"
                  style={{
                    width: 130,
                    borderColor: selectedYear ? "#be9545" : undefined,
                    color: selectedYear ? "#be9545" : undefined,
                    fontWeight: selectedYear ? 600 : undefined,
                  }}
                  value={selectedYear}
                  onChange={e => { setSelectedYear(e.target.value); resetPage() }}
                >
                  {YEARS.map(y => (
                    <option key={y.value} value={y.value}>
                      {y.label}
                    </option>
                  ))}
                </Input>
              </div>

              {/* Right — Add Button */}
              {checkAccess(4, "add") && (
                <Button
                  className="btn-blue rounded-4"
                  onClick={() => navigate("/cold-calling/add")}
                >
                  <FaPlus className="me-1" /> Add Cold Call
                </Button>
              )}
            </div>

            {/* ── Table ── */}
            <Table bordered hover responsive>
              <thead className="table-dark">
                <tr>
                  <th>#</th>
                  <th>Date</th>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Discussion</th>
                  <th>Area</th>
                  <th>Added By</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.length === 0 ? (
                  <tr>
                    <td className="text-center py-4" colSpan={8}>
                      {activeFilters > 0
                        ? "No results found for the selected filters"
                        : "No Cold Call Found"}
                    </td>
                  </tr>
                ) : (
                  paginatedData.map((item, index) => (
                    <tr key={item.id}>
                      {/* ── Serial number continues across pages ── */}
                      <td>{start + index + 1}</td>
                      <td>{moment(item.date).format("DD-MM-YYYY")}</td>
                      <td>{item.name}</td>
                      <td className="text-capitalize">{item.type}</td>
                      <td>{item.machineName}</td>
                      <td>{item.area}</td>
                      <td>{item.addedBy?.name}</td>
                      <td>
                        {checkAccess(4, "edit") && (
                          <Button
                            color="info"
                            onClick={() =>
                              navigate(`/cold-calling/edit/${item.id}`)
                            }
                          >
                            <FaEdit />
                          </Button>
                        )}
                        {checkAccess(4, "delete") && (
                          <Button
                            color="danger"
                            className="ms-2"
                            onClick={() => handleDelete(item, item?.id)}
                          >
                            <MdDelete />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>

            {/* ── Pagination ── */}
            <nav className="d-flex justify-content-between align-items-center mt-3">
              <h6 className="mb-0">
                Showing {filteredData.length === 0 ? 0 : start + 1} to{" "}
                {Math.min(start + pageSize, filteredData.length)} of{" "}
                {filteredData.length} entries
              </h6>

              <ul className="pagination mb-0">
                <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  >
                    Previous
                  </button>
                </li>

                {[...Array(totalPages)].map((_, i) => {
                  const pageNum = i + 1
                  return (
                    <li
                      key={pageNum}
                      className={`page-item ${pageNum === currentPage ? "active" : ""}`}
                    >
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </button>
                    </li>
                  )
                })}

                <li
                  className={`page-item ${currentPage === totalPages || totalPages === 0 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() =>
                      setCurrentPage(prev => Math.min(prev + 1, totalPages))
                    }
                  >
                    Next
                  </button>
                </li>
              </ul>
            </nav>

          </CardBody>
        </Card>
      </div>
    </PermittedComponent>
  )
}

export default ColdCallingIndex